// Public category pages: /topics (all topics) and /topic/:slug (one topic).
// Built for search: one clear H1, a short description, every show in the topic, first episodes and creators.
// In production these are server-rendered with the same content.
import { useEffect, type CSSProperties } from 'react';
import { useCatalog } from '../lib/catalog';
import { clock } from '../lib/format';
import { Link } from '../lib/router';
import { Icon } from '../components/Icon';
import { PosterLink } from '../components/Poster';
import { AppBar, PublicFooter, PublicHeader, StoreButtons } from '../components/PublicChrome';

// What each topic covers, in plain words (shown under the H1 and used as the page description).
const TOPIC_INTRO: Record<string, string> = {
  drama: 'Full stories told in 2 to 3 minute episodes: family, love, first jobs and twists you will not see coming.',
  college: 'Hostel life, exams, friendships and first internships: stories and survival tips for every college student.',
  comedy: 'Short sketches about Indian homes, parents, weddings and Sundays that the whole family will relate to.',
  govt: 'PAN, Aadhaar, Ayushman card, ration card, pensions and government schemes, explained step by step so you can do it yourself, often from home.',
  money: 'Saving, SIP, mutual funds, the share market and loans in simple words, so you can make smarter choices with your money.',
  career: 'Resumes, interviews, job search and workplace skills to help you get a job or grow in the one you have.',
  english: 'Everyday spoken English for work, phone calls and interviews, a few minutes at a time.',
  earn: 'Instagram, YouTube and other ways to earn online, with honest advice on what works and what to avoid.',
  business: 'Starting and growing a small business: ideas, licences, pricing, selling online and managing money.',
  hacks: 'Small, smart tips for daily life that save time and money at home and outside.',
  health: 'Simple fitness, food and wellbeing habits you can start today, with no equipment.',
};

function setMeta(title: string, description: string) {
  document.title = title;
  let m = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
  m.content = description;
}

export function TopicsIndex() {
  const { shows: SHOWS, episodes: EPISODES, topics: TOPICS } = useCatalog();
  useEffect(() => { setMeta('All topics · Kuch Naya', 'Short shows on Kuch Naya: micro dramas, college life, comedy, government services, money, jobs, English and more.'); }, []);
  return (
    <div className="landing lp pub">
      <PublicHeader />
      <section className="wrap pub-hero">
        <span className="lp-eyebrow"><Icon name="grid" size={14} /> {TOPICS.length} topics</span>
        <h1>What do you want to watch today?</h1>
        <p className="lp-sub">Pick a topic to see every show in it. Dramas, comedy, college stories or smart how-tos: every episode is short and made by a trusted creator.</p>
      </section>
      <section className="wrap l-section">
        <div className="topic-hub">
          {TOPICS.map((t) => {
            const shows = SHOWS.filter((s) => s.topic === t.slug);
            const eps = EPISODES.filter((e) => shows.some((s) => s.id === e.showId)).length;
            return (
              <Link key={t.slug} to={`/topic/${t.slug}`} className="topic-hub-card" style={{ '--poster': t.color } as CSSProperties}>
                <span className="topic-hub-glyph">{t.glyph}</span>
                <b>{t.name}</b>
                <small>{TOPIC_INTRO[t.slug]?.split('.')[0] || t.blurb}.</small>
                <span className="topic-hub-meta">{shows.length} {shows.length === 1 ? 'show' : 'shows'} · {eps} episodes <Icon name="right" size={14} /></span>
              </Link>
            );
          })}
        </div>
      </section>
      <AppCta />
      <PublicFooter />
      <AppBar />
    </div>
  );
}

export function TopicPage({ slug }: { slug: string }) {
  const { shows: SHOWS, episodes: EPISODES, topics: TOPICS } = useCatalog();
  const topic = TOPICS.find((t) => t.slug === slug);
  const shows = SHOWS.filter((s) => s.topic === slug);
  const firstEps = shows.map((s) => ({ show: s, ep: EPISODES.find((e) => e.showId === s.id && e.no === 1)! })).filter((x) => x.ep);
  const epCount = EPISODES.filter((e) => shows.some((s) => s.id === e.showId)).length;
  const creators = [...new Map(shows.map((s) => [s.creator.id, s.creator])).values()];
  useEffect(() => {
    if (topic) setMeta(`${topic.name}: short shows · Kuch Naya`, TOPIC_INTRO[slug] || topic.blurb);
  }, [topic, slug]);

  if (!topic) {
    return (
      <div className="landing lp pub">
        <PublicHeader />
        <section className="wrap pub-hero"><h1>Topic not found</h1><p className="lp-sub">That topic does not exist. See <Link to="/topics">all topics</Link>.</p></section>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="landing lp pub">
      <PublicHeader />
      <nav className="wrap crumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><Link to="/topics">Topics</Link><span aria-hidden="true">/</span><span aria-current="page">{topic.name}</span></nav>

      <section className="wrap topic-hero" style={{ '--poster': topic.color } as CSSProperties}>
        <span className="topic-hero-glyph" aria-hidden="true">{topic.glyph}</span>
        <div className="topic-hero-copy">
          <h1>{topic.name}</h1>
          <p className="lp-sub">{TOPIC_INTRO[slug] || topic.blurb}</p>
          <ul className="topic-hero-facts"><li><b>{shows.length}</b> shows</li><li><b>{epCount}</b> episodes</li><li><b>{creators.length}</b> {creators.length === 1 ? 'creator' : 'creators'}</li></ul>
          <div className="row-wrap">
            <Link to={`/explore/${slug}`} className="btn btn-lg btn-primary"><Icon name="play" size={16} /> Start watching free</Link>
          </div>
        </div>
      </section>

      <section className="wrap l-section">
        <h2>Shows in {topic.name}</h2>
        <div className="poster-grid topic-shows">
          {shows.map((s) => <PosterLink key={s.id} show={s} size="fill" meta={`${s.episodeCount} episodes${s.access === 'free' ? ' · Free' : ''}`} />)}
        </div>
      </section>

      <section className="wrap l-section">
        <h2>Start with episode 1</h2>
        <ol className="episodes topic-eps">
          {firstEps.map(({ show, ep }) => (
            <li key={ep.id}>
              <Link to={`/watch/${show.id}/1`} className="episode">
                <span className="ep-thumb" style={{ '--poster': show.color } as CSSProperties}><Icon name="play" size={14} /></span>
                <span className="ep-text"><b>{ep.title}</b><small>{show.title} · {clock(ep.durationS)}{ep.isFreePreview ? ' · Free' : ''}</small></span>
                <Icon name="right" size={18} />
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section className="wrap l-section">
        <h2>Creators</h2>
        <ul className="topic-creators">
          {creators.map((c) => {
            const cs = shows.filter((s) => s.creator.id === c.id);
            return (
              <li key={c.id}><span className="avatar">{c.name[0]}</span><span><b>{c.name}</b><small className="muted">{cs.map((s) => s.title).join(' · ')}</small></span></li>
            );
          })}
        </ul>
      </section>

      <section className="wrap l-section">
        <h2>More topics</h2>
        <div className="lp-pills">
          {TOPICS.filter((t) => t.slug !== slug).map((t) => (
            <Link key={t.slug} to={`/topic/${t.slug}`} className="lp-pill"><span className="lp-pill-dot" style={{ background: t.color }}>{t.glyph}</span>{t.name}</Link>
          ))}
        </div>
      </section>

      <AppCta />
      <PublicFooter />
      <AppBar />
    </div>
  );
}

/** Download prompt used at the bottom of public pages. */
export function AppCta() {
  return (
    <section className="wrap l-section">
      <div className="lp-app">
        <div className="lp-app-copy">
          <h2>Watch on the Kuch Naya app</h2>
          <p>Watch offline, get alerts when new episodes drop and keep your daily streak. Free to download.</p>
          <StoreButtons />
        </div>
      </div>
    </section>
  );
}
